import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import AlertModal from '../components/AlertModal';
import ConfirmModal, { ConfirmModalVariant } from '../components/ConfirmModal';
import { setGlobalAlertHandler } from '../utils/modalBridge';

export interface AlertOptions {
  title: string;
  message: string;
  variant?: 'success' | 'danger' | 'warning' | 'info';
  details?: string;
}

export interface ConfirmOptions {
  title: string;
  message: string;
  details?: string;
  variant?: ConfirmModalVariant;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ModalContextType {
  showAlert: (options: AlertOptions) => void;
  showConfirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [alertOptions, setAlertOptions] = useState<AlertOptions | null>(null);
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions | null>(null);
  const confirmResolveRef = useRef<((value: boolean) => void) | null>(null);

  const showAlert = useCallback((options: AlertOptions) => {
    setAlertOptions({
      variant: 'info',
      ...options,
    });
  }, []);

  const hideAlert = useCallback(() => {
    setAlertOptions(null);
  }, []);

  const showConfirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmOptions({
        variant: 'primary',
        ...options,
      });
    });
  }, []);

  const handleConfirmHide = useCallback(() => {
    setConfirmOptions(null);
    if (confirmResolveRef.current) {
      confirmResolveRef.current(false);
      confirmResolveRef.current = null;
    }
  }, []);

  const handleConfirmConfirm = useCallback(() => {
    if (confirmResolveRef.current) {
      confirmResolveRef.current(true);
      confirmResolveRef.current = null;
    }
    setConfirmOptions(null);
  }, []);

  useEffect(() => {
    setGlobalAlertHandler(showAlert);
    return () => setGlobalAlertHandler(null);
  }, [showAlert]);

  return (
    <ModalContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      {alertOptions && (
        <AlertModal
          show={true}
          onHide={hideAlert}
          title={alertOptions.title}
          message={alertOptions.message}
          variant={alertOptions.variant}
          details={alertOptions.details}
        />
      )}
      {confirmOptions && (
        <ConfirmModal
          show={true}
          onHide={handleConfirmHide}
          onConfirm={handleConfirmConfirm}
          title={confirmOptions.title}
          message={confirmOptions.message}
          details={confirmOptions.details}
          variant={confirmOptions.variant}
          confirmLabel={confirmOptions.confirmLabel}
          cancelLabel={confirmOptions.cancelLabel}
        />
      )}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextType => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
